/**
 * Partners Service
 * 
 * Handles all partner-related API calls.
 * Partners are business entities synced from Google Sheets.
 * 
 * @module services/partners
 * 
 * @example
 * // Search partners
 * const partners = await partnersService.search('pizza');
 * 
 * @example
 * // Get partner by ID
 * const partner = await partnersService.getById(123);
 */

import { apiClient, ApiResponse } from './api';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Partner entity
 * Represents a business partner from the hub_partners table
 */
export interface Partner {
  businessId: number;
  businessNameHe: string | null;
  businessNameAr: string | null;
  businessNameEn: string | null;
  businessAddress: string | null;
  businessType: string | null;
  phone1: string | null;
  phone2: string | null;
  phone3: string | null;
  phone4: string | null;
  logoUrl: string | null;
  area: string | null;
  areaId: number | null;
  franchiseId: string | null;
  franchiseName: string | null;
  zoneId: string | null;
  zoneName: string | null;
  partnerOwnerPhone: string | null;
  partnerUsername: string | null;
  countryId: string | null;
  erpNumber: string | null;
  contractorId: string | null;
  nativeLanguage: string | null;
  readiness: string | null;
  isReady: string | null;
  isDeleted: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Search parameters for partner search
 */
export interface PartnerSearchParams {
  /** Search query (min 1 character) */
  query: string;
  /** Maximum results to return (default: 20) */
  limit?: number;
}

// =============================================================================
// PARTNERS SERVICE
// =============================================================================

/**
 * Partners service object
 * 
 * Contains all partner-related methods.
 */
export const partnersService = {
  // ===========================================================================
  // SEARCH
  // ===========================================================================

  /**
   * Search partners by query string
   * 
   * Searches in: business_id, business_name_he, business_name_ar, 
   * business_name_en, business_address
   * 
   * @param params - Search parameters
   * @returns Promise resolving to list of matching partners
   * 
   * @example
   * const partners = await partnersService.search({ query: 'pizza', limit: 10 });
   * partners.forEach(p => console.log(p.businessNameHe));
   */
  search: async (params: PartnerSearchParams): Promise<Partner[]> => {
    if (!params.query || params.query.trim().length < 1) {
      return [];
    }
    
    const response = await apiClient.get<ApiResponse<Partner[]>>('/partners/search', {
      params: {
        q: params.query.trim(),
        limit: params.limit || 20,
      },
    });
    return response.data.data;
  },

  // ===========================================================================
  // READ OPERATIONS
  // ===========================================================================

  /**
   * Get all active partners
   * 
   * @returns Promise resolving to list of all active partners
   * 
   * @example
   * const allPartners = await partnersService.getAll();
   */
  getAll: async (): Promise<Partner[]> => {
    const response = await apiClient.get<ApiResponse<Partner[]>>('/partners');
    return response.data.data;
  },

  /**
   * Get a single partner by business ID
   * 
   * @param id - Business ID (businessId)
   * @returns Promise resolving to partner data
   * @throws Error if partner not found (404)
   * 
   * @example
   * const partner = await partnersService.getById(12345);
   * console.log('Partner:', partner.businessNameHe);
   */
  getById: async (id: number): Promise<Partner> => {
    const response = await apiClient.get<ApiResponse<Partner>>(`/partners/${id}`);
    return response.data.data;
  },

  /**
   * Get a single partner by business ID
   * 
   * @param businessId - Business ID from HAAT system
   * @returns Promise resolving to partner data or null
   * 
   * @example
   * const partner = await partnersService.getByBusinessId(12345);
   */
  getByBusinessId: async (businessId: number): Promise<Partner | null> => {
    const response = await apiClient.get<ApiResponse<Partner | null>>(`/partners/business/${businessId}`);
    return response.data.data;
  },

  /**
   * Get partners by franchise ID
   * 
   * @param franchiseId - Franchise ID
   * @returns Promise resolving to list of partners
   * 
   * @example
   * const franchisePartners = await partnersService.getByFranchise('F001');
   */
  getByFranchise: async (franchiseId: string): Promise<Partner[]> => {
    const response = await apiClient.get<ApiResponse<Partner[]>>(`/partners/by-franchise/${franchiseId}`);
    return response.data.data;
  },

  /**
   * Get partners by area ID
   * 
   * @param areaId - Area ID
   * @returns Promise resolving to list of partners
   * 
   * @example
   * const areaPartners = await partnersService.getByArea(19);
   */
  getByArea: async (areaId: number): Promise<Partner[]> => {
    const response = await apiClient.get<ApiResponse<Partner[]>>(`/partners/by-area/${areaId}`);
    return response.data.data;
  },

  /**
   * Find partner by phone number
   * 
   * @param phone - Phone number to search
   * @returns Promise resolving to partner or null
   * 
   * @example
   * const partner = await partnersService.getByPhone('0501234567');
   */
  getByPhone: async (phone: string): Promise<Partner | null> => {
    const response = await apiClient.get<ApiResponse<Partner | null>>('/partners/by-phone', {
      params: { phone },
    });
    return response.data.data;
  },

  // ===========================================================================
  // SYNC
  // ===========================================================================

  /**
   * Trigger sync from Google Sheets
   * 
   * @returns Promise resolving to sync result
   * 
   * @example
   * const result = await partnersService.syncFromGoogleSheet();
   * console.log(`Synced ${result.synced} partners`);
   */
  syncFromGoogleSheet: async (): Promise<{ success: boolean; synced: number; errors: number }> => {
    const response = await apiClient.post<ApiResponse<{ success: boolean; synced: number; errors: number }>>('/partners/sync');
    return response.data.data;
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get display name for a partner
 * Returns the first available name (Hebrew > Arabic > English > ID)
 */
export const getPartnerDisplayName = (partner: Partner): string => {
  return partner.businessNameHe || partner.businessNameAr || partner.businessNameEn || `Partner #${partner.businessId}`;
};

// =============================================================================
// EXPORTS
// =============================================================================

export default partnersService;

